import { useLoaderData } from 'react-router-dom';
import { loadHomeBundle, type HomeBundle } from '../../data/loaders';
import { MetricsStrip } from '../../components/MetricsStrip/MetricsStrip';
import { Leaderboard } from '../../components/Leaderboard/Leaderboard';
import { HowToRead } from '../../components/HowToRead/HowToRead';
import { FeaturedQuotes } from '../../components/FeaturedQuote/FeaturedQuote';
import { formatGeneratedAt, formatLargeInt, formatWeekRange } from '../../lib/format';
import { usePageMeta } from '../../lib/use-page-meta';
import styles from './HomePage.module.css';

export async function homeLoader(): Promise<HomeBundle> {
  return loadHomeBundle();
}

export function HomePage() {
  const { meta, leaderboard, topModels } = useLoaderData() as HomeBundle;

  usePageMeta({
    title: `ViBE — Reception benchmark · Issue ${String(meta.week).padStart(2, '0')}`,
    description: `A one-week Twitter benchmark for frontier model families. Issue ${meta.week}: ${meta.ranked_models} families ranked across ${meta.total_scored_mentions.toLocaleString('en-US')} scored mentions.`,
  });

  return (
    <article className={styles.page}>
      {/* ───── Title block — benchmark card, not editorial hero ───── */}
      <section className={`${styles.title} container-wide`}>
        <div className={styles.titleBlock}>
          <p className={styles.eyebrow}>Reception benchmark · issue {String(meta.week).padStart(2, '0')}</p>
          <h1 className={styles.heading}>
            Reception benchmark for frontier model families
          </h1>
          <p className={styles.standfirst}>
            ViBE scores how frontier model families are received in public discussion. Recommendations, complaints,
            reliability gripes, trust, and day-to-day usage intent are pulled from public posts on Twitter,
            labelled by an independent judge model, and aggregated by bootstrap into a single reception score.
            This release is Twitter-only and family-level by design.
          </p>
        </div>

        <aside className={styles.titleMeta} aria-label="Collection window">
          <dl className={styles.titleMetaList}>
            <div>
              <dt>Collection window</dt>
              <dd>{formatWeekRange(meta.week_slug)}</dd>
            </div>
            <div>
              <dt>Formula</dt>
              <dd className={styles.titleMetaMono}>{meta.formula_version}</dd>
            </div>
            <div>
              <dt>Judge</dt>
              <dd className={styles.titleMetaMono}>{meta.judge_models[0]}</dd>
            </div>
          </dl>
        </aside>
      </section>

      {/* ───── Metrics strip ──────────────────────────────── */}
      <div className="container-wide">
        <MetricsStrip meta={meta} />
      </div>

      {/* ───── Primary leaderboard ────────────────────────── */}
      <section className={`${styles.leaderboardSection} container-wide`}>
        <header className={styles.sectionHeader}>
          <div>
            <p className={styles.sectionEyebrow}>01 · Ranking</p>
            <h2 className={styles.sectionHeading}>Ranked families</h2>
          </div>
          <p className={styles.sectionLede}>
            {leaderboard.rows.length} families with enough high-confidence public discussion to rank with a 95% bootstrap
            interval. Confidence intervals are computed across {formatLargeInt(meta.bootstrap_iterations)} resamples.
            Families whose intervals overlap are not statistically distinguishable — the week-to-week order between them
            is provisional, not declarative.
          </p>
        </header>

        <Leaderboard rows={leaderboard.rows} caption={`${leaderboard.rows.length} ranked families · sorted by reception score`} />
      </section>

      {/* ───── Tracked but not ranked ─────────────────────── */}
      {leaderboard.tracked_unranked.length > 0 ? (
        <section className={`${styles.unrankedSection} container-wide`}>
          <header className={styles.sectionHeader}>
            <div>
              <p className={styles.sectionEyebrow}>02 · Held back</p>
              <h2 className={styles.sectionHeading}>Tracked families, insufficient evidence</h2>
            </div>
            <p className={styles.sectionLede}>
              These families are watched the same way the ranked families are watched. Their high-confidence sample size
              is below the threshold for a stable rank this week. Their observed scores are shown for the curious. They
              are not penalised. They are paused.
            </p>
          </header>

          <Leaderboard
            rows={leaderboard.tracked_unranked}
            caption={`${leaderboard.tracked_unranked.length} tracked families · insufficient evidence to rank`}
            variant="subordinate"
          />
        </section>
      ) : null}

      {/* ───── How to read ────────────────────────────────── */}
      <div className="container-wide">
        <HowToRead />
      </div>

      {/* ───── Featured discourse ─────────────────────────── */}
      <div className="container-wide">
        <FeaturedQuotes models={topModels} />
      </div>

      {/* ───── Bulletin footnote ──────────────────────────── */}
      <section className={`${styles.bulletinFootnote} container-wide`}>
        <p>
          Issue {String(meta.week).padStart(2, '0')} · {formatWeekRange(meta.week_slug)} · formula{' '}
          <code>{meta.formula_version}</code> · {formatLargeInt(meta.bootstrap_iterations)} bootstrap iterations ·
          judge <code>{meta.judge_models[0]}</code> · generated {formatGeneratedAt(meta.generated_at)}
        </p>
      </section>
    </article>
  );
}
